import '@eg-maps/google-maps';
import '@eg-maps/mapbox';
import * as React from 'react';
import { EGMap } from '@eg-maps/core';
import { classNames } from '@egds/react-core/utils';
import { useBrand } from '@shared-ui/brand-context';
import { EGDistances } from 'eg-distances';
import { mergeAndConcat } from 'merge-anything';
import { defaultEGMapsConfig, sortDeeplyObjectAndArray, stringify } from '../../common/config/index.esm.js';
import 'react-dom';
import '@egds/react-core/icons';
import { MapContext } from '../../map/index.esm.js';
import { MapType } from '../../types/index.esm.js';
import styles from '../../../../../styles/egds-maps-map.css';
const getClassNameList = classNames.bind(styles);
const defaultMapboxStyle = 'mapbox://styles/mapbox/streets-v11';
const gmapCfg = () => ({
  mapProvider: 'google-maps',
  googleMaps: {
    gmapsAddConfig: {
      streetViewControl: true,
      streetViewControlOptions: {
        position: window?.google?.maps?.ControlPosition.RIGHT_TOP
      },
      zoomControlOptions: {
        position: window?.google?.maps?.ControlPosition.RIGHT_TOP
      },
      scaleControl: true
    }
  }
});
const mapboxCfg = accessToken => ({
  mapProvider: 'mapbox',
  mapbox: {
    accessToken,
    mapboxAddConf: {
      style: defaultMapboxStyle
    }
  }
});
const egDistancesConfig = (provider, accessToken) => ({
  routes: {
    egDistances: provider === 'mapbox' ? new EGDistances({
      provider: EGDistances.providers.mapbox,
      providers: {
        [EGDistances.providers.mapbox]: {
          key: accessToken
        }
      }
    }) : new EGDistances({
      provider: EGDistances.providers.google
    })
  }
});
const MapContainer = ({
  height = '100%',
  width = '100%',
  egDistancesEnabled = false,
  initialViewport,
  accessToken,
  children,
  mapType,
  features,
  onFeaturesPushed,
  mapConfig = {},
  className,
  beforeInit,
  onInit,
  clientPadding,
  onError
}) => {
  const mapProvider = mapType === MapType.GOOGLE ? 'google-maps' : 'mapbox';
  const [mapContext, setMapContext] = React.useState();
  const [initialConfig, setInitialConfig] = React.useState();
  const [providedConfig, setProvidedConfig] = React.useState();
  const mapMemoValue = React.useMemo(() => ({
    mapContext,
    setMapContext
  }), [mapContext, setMapContext]);
  const mapRef = React.useRef();
  const brand = useBrand();
  const idSuffix = React.useMemo(() => Math.floor(Math.random() * 90), []);
  const baseConfig = React.useMemo(() => {
    let ret;
    if (mapRef.current) {
      const egDistancesCfg = egDistancesEnabled ? egDistancesConfig(mapProvider, accessToken) : {};
      const mapProviderCfg = mapProvider === 'mapbox' ? mapboxCfg(accessToken) : gmapCfg();
      const defaultCfg = defaultEGMapsConfig(brand, cssPropertyName => getComputedStyle(mapRef.current)?.getPropertyValue(cssPropertyName));
      ret = mergeAndConcat(defaultCfg, mapProviderCfg, egDistancesCfg);
    }
    return ret;
  }, [mapRef.current, brand, mapProvider, accessToken, egDistancesEnabled]);
  React.useEffect(() => {
    if (mapConfig) {
      const cfg = mapConfig ? sortDeeplyObjectAndArray(mapConfig) : {};
      if (providedConfig === undefined || stringify(cfg) !== stringify(providedConfig)) {
        setProvidedConfig(cfg);
      }
    }
  }, [mapConfig, providedConfig]);
  const config = React.useMemo(() => {
    let ret;
    if (baseConfig) {
      const configs = [];
      if (providedConfig) {
        configs.push(providedConfig);
        if (providedConfig.googleMaps?.gmapsAddConfig?.mapId === null) configs.push({
          googleMaps: {
            gmapsAddConfig: {
              mapId: undefined
            }
          }
        });
        if (providedConfig.mapbox?.mapboxAddConf?.style === null) configs.push({
          mapbox: {
            mapboxAddConf: {
              style: undefined
            }
          }
        });
      }
      ret = mergeAndConcat(baseConfig, ...configs);
    }
    return ret;
  }, [baseConfig, providedConfig]);
  React.useEffect(() => {
    const {
      mapObj
    } = mapContext || {};
    if (config && config !== initialConfig) {
      if (mapObj) {
        mapObj.updateConfiguration(config);
      } else if (!initialConfig) {
        setInitialConfig(config);
      }
    }
  }, [initialConfig, config, mapContext]);
  React.useEffect(() => {
    if (initialConfig) {
      if (mapContext?.mapObj) {
        mapContext?.mapObj.unbindAll();
      }
      try {
        const mapObj = new EGMap(initialConfig);
        if (beforeInit) beforeInit(mapObj);
        mapObj.initMap(mapRef.current, true, initialViewport, clientPadding).then(() => {
          if (onInit) onInit(mapObj);
          setMapContext({
            mapObj,
            mapConfig: initialConfig
          });
        }).catch(err => {
          if (typeof onError === 'function') onError(err);
        });
      } catch (err) {
        if (typeof onError === 'function') onError(err);
      }
      return () => {
        mapContext?.mapObj?.unbindAll();
      };
    }
  }, [initialConfig]);
  React.useEffect(() => {
    if (mapContext?.mapObj && features) {
      const bounds = mapContext?.mapObj.pushFeatureData(features, 'replace');
      if (bounds) onFeaturesPushed?.(mapContext?.mapObj, bounds);
    }
  }, [mapContext, features, onFeaturesPushed]);
  return React.createElement("div", {
    className: getClassNameList('map-container', 'map', className),
    style: {
      width,
      height
    },
    id: `map-container-${idSuffix}`,
    ref: mapRef
  }, mapContext?.mapObj && React.createElement(MapContext.Provider, {
    value: mapMemoValue
  }, children));
};
export { MapContainer };